import React, { useCallback, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WAWidgetSizeToTaskSingleSize } from "../../WidgetAdd/utils/WAWidgetSizeToTaskSingleSize";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { WidgetTaskSingle } from "widgets";
import { WAKPISettings } from "./WAKPISettings";
import { WAKPIQuery } from "./__generated__/WAKPIQuery.graphql";
import { WAKPIMutation } from "./__generated__/WAKPIMutation.graphql";
import { WidgetScreen } from "../../common/WidgetScreen";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { taskSingleWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/taskSingleWidgetSizes";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddKPI">;
  route: UserLoggedInNavigatorRouteProp<"WidgetAddKPI">;
};

export const WAKPI = withWebModal(WAKPIC);
function WAKPIC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAKPI {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAKPI(props: Props & { fetchKey: string }) {
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const [localSectionIds, setSectionIds] = useState<string[]>([]);
  const data = useLazyLoadQuery<WAKPIQuery>(
    graphql`
      query WAKPIQuery(
        $storecastId: ID!
        $taskId: ID!
        $input: PreviewWidgetTaskSingleInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetTaskSingle(input: $input) {
            activeSectionVersions {
              id
              title
              section {
                id
              }
            }
            ...WAKPISettings_widgetTaskSingle
            ...WidgetTaskSingle_widgetTaskSingle
          }
        }
        task(id: $taskId) {
          id
        }
      }
    `,
    {
      input: {
        grouping: "SECTION",
        id: "dummyId",
        sectionIds: [],
        taskId: props.route.params.taskId,
        taskSingleSize: "TWO_X_ONE",
        teamIds: [],
      },
      taskId: props.route.params.taskId,
      storecastId: props.route.params.storecastId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [createWidgetKPI, loading] = useMutation<WAKPIMutation>(graphql`
    mutation WAKPIMutation(
      $storecastId: ID!
      $input: CreateWidgetTaskSingleInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        createWidgetTaskSingle(widget: $input) {
          id
        }
      }
    }
  `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidgetKPI({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToTaskSingleSize(size),
            taskId: props.route.params.taskId,
            grouping: "SECTION",
            sectionIds: localSectionIds,
          },
        },
        onError: (e) => {
          console.log("e", e);
        },
        onCompleted: () => props.navigation.pop(2),
      });
    },
    [
      createWidgetKPI,
      localSectionIds,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
      props.route.params.taskId,
    ]
  );

  const intl_tokens = useIntlTokens();

  const sectionIds = useMemo(
    () =>
      localSectionIds.length === 0
        ? data.dashboard.previewWidgetTaskSingle.activeSectionVersions.map(
            (s) => s.section.id
          )
        : localSectionIds,
    [
      localSectionIds,
      data.dashboard.previewWidgetTaskSingle.activeSectionVersions,
    ]
  );

  return (
    <WidgetScreen
      preview={
        <WASizePreview
          loading={loading}
          onCreate={onCreate}
          maxSize={props.route.params.maxSize}
          unitSize={props.route.params.unitSize}
          widgetSizes={taskSingleWidgetSizes}
        >
          {(size) => (
            <WidgetTaskSingle
              mode="IDLE"
              widget={data.dashboard.previewWidgetTaskSingle}
              fetchKey={props.fetchKey}
              storecastId={props.route.params.storecastId}
              clientFilterSectionIds={sectionIds}
              clientForceSize={size.w === 2 ? "TWO_X_ONE" : "ONE_X_ONE"}
            />
          )}
        </WASizePreview>
      }
      settings={
        <>
          {!isCompact ? (
            <WAFakeHeader title={intl_tokens.SelectMeasurementAreas} />
          ) : null}
          <WAKPISettings
            widget={data.dashboard.previewWidgetTaskSingle}
            sectionIds={localSectionIds}
            setSectionIds={setSectionIds}
          />
        </>
      }
    />
  );
}
